import {
    Box,
    FormControl,
    FormHelperText,
    InputBase,
    InputBaseProps,
    InputLabel,
    SxProps,
    InputAdornment,
} from '@mui/material';
import get from 'lodash/get';
import useTranslation from 'next-translate/useTranslation';
import { FC, ReactNode } from 'react';
import { FieldError } from 'react-hook-form';
import { getErrorMessage } from '@utils/form';
import { sxs } from '@utils/mui/sx';
import { FormProps, Validators } from '@utils/types/form';

export type BaseFieldInterfaceProps = Validators &
    Pick<
        InputBaseProps,
        | 'autoComplete'
        | 'autoFocus'
        | 'defaultValue'
        | 'disabled'
        | 'endAdornment'
        | 'inputProps'
        | 'multiline'
        | 'placeholder'
        | 'readOnly'
        | 'rows'
        | 'startAdornment'
        | 'type'
    > & {
        error?: string;
        inputSx?: SxProps;
        label?: ReactNode;
        labelSx?: SxProps;
        name: string;
    };

export type BaseFieldProps = BaseFieldInterfaceProps & FormProps;

const BaseField: FC<BaseFieldProps> = ({
    autoComplete,
    autoFocus,
    defaultValue,
    disabled,
    endAdornment,
    formDisabled,
    formState,
    inputProps,
    inputSx = {},
    label,
    labelSx = {},
    max,
    maxLength,
    min,
    minLength,
    multiline,
    name,
    pattern,
    placeholder,
    readOnly,
    register,
    required,
    rows,
    startAdornment,
    type,
    validate,
    value,
}) => {
    const error = get(formState.errors, name) as FieldError;
    const { t } = useTranslation('common');
    const { ref, ...rest } = register(name, {
        max,
        maxLength,
        min,
        minLength,
        pattern,
        required,
        validate,
    });

    const errorMessage = getErrorMessage({ error, max, maxLength, min, minLength, t });

    // Ensure consistent spacing after any start adornment
    const startAdornmentNode = startAdornment ? (
        <InputAdornment position="start" sx={{ mr: 1 }}>
            {startAdornment}
        </InputAdornment>
    ) : undefined;

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl disabled={disabled || formDisabled} variant="standard" fullWidth>
                {label && (
                    <InputLabel htmlFor={name} sx={labelSx} shrink>
                        {label}
                        {!required && (
                            <Box component="span" sx={{ color: `grey.400` }}>
                                {` ${t('form.optional')}`}
                            </Box>
                        )}
                    </InputLabel>
                )}
                <InputBase
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    data-test-id={`${name}-field`}
                    defaultValue={defaultValue}
                    endAdornment={endAdornment}
                    id={name}
                    inputProps={inputProps}
                    inputRef={ref}
                    multiline={multiline}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    rows={rows}
                    startAdornment={startAdornmentNode}
                    sx={sxs([
                        { bgcolor: 'grey.800' },
                        inputSx,
                        'textarea' === type ? { p: 1 } : null,
                        errorMessage ? { borderColor: 'error.main' } : null,
                    ])}
                    type={type}
                    value={value}
                    {...rest}
                />
                {errorMessage && (
                    <FormHelperText sx={{ color: 'error.main' }} error>
                        {errorMessage}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    );
};

export default BaseField;
